import React from 'react';

import { UUID_PATTERN } from '@vlabs/shared/validators';
import { Control } from '@vlabs/uikit';
import { Select } from '@vlabs/uikit/components/controls/select/Select';
import { Controller, useForm } from 'react-hook-form';

import {
  ANALYTICS_TYPES,
  loadHandlerOptions,
} from '@vlabs/monokit/entities/sources';
import { selectMenuPortalStyles } from '@vlabs/monokit/shared/ui/select-portal';

import { useCreateStreamTranslation } from '../i18n';
import st from './NewAnalyticsModal.module.sass';

export interface NewAnalyticsValues {
  type: string;
  handlerId: string;
}

interface NewAnalyticsModalProps {
  onCancel: VoidFunction;
  onSubmit: (values: NewAnalyticsValues) => void;
}

function NewAnalyticsModal({
  onCancel,
  onSubmit,
}: NewAnalyticsModalProps): React.JSX.Element {
  const { t } = useCreateStreamTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<NewAnalyticsValues>({
    defaultValues: { type: ANALYTICS_TYPES[0].value, handlerId: '' },
  });

  const submit = handleSubmit((values: NewAnalyticsValues) => {
    onSubmit(values);
  });

  return (
    <form onSubmit={submit}>
      <div className={st.Root}>
        <div className={st.Content}>
          <h5 className={st.Title}>
            {t('create_stream_form.new_analytics.title')}
          </h5>

          <div className={st.Field}>
            <Controller
              control={control}
              name="type"
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t('create_stream_form.new_analytics.type')}
                  onChange={(option) => onChange(option?.value ?? '')}
                  options={ANALYTICS_TYPES}
                  value={ANALYTICS_TYPES.find((option) => option.value === value)}
                />
              )}
              rules={{
                required: t('create_stream_form.new_analytics.validation.required'),
              }}
            />
          </div>

          <div className={st.Field}>
            <Controller
              control={control}
              name="handlerId"
              render={({ field: { value, onChange } }) => (
                <Select
                  defaultOptions
                  errorMessage={errors.handlerId?.message}
                  isAsync
                  isClearable
                  isCreatable
                  label={t('create_stream_form.new_analytics.handler')}
                  loadOptions={loadHandlerOptions}
                  menuPortalTarget={document.body}
                  onChange={(option) => onChange(option?.value ?? '')}
                  placeholder={t('create_stream_form.new_analytics.handler')}
                  styles={selectMenuPortalStyles}
                  value={value ? { label: value, value } : undefined}
                />
              )}
              rules={{
                pattern: {
                  message: t('create_stream_form.new_analytics.validation.uuid'),
                  value: UUID_PATTERN,
                },
                required: t('create_stream_form.new_analytics.validation.required'),
              }}
            />
          </div>
        </div>

        <div className={st.Actions}>
          <Control.Button type="submit">
            {t('create_stream_form.new_analytics.on_add')}
          </Control.Button>

          <Control.Button
            kind="secondary"
            onClick={onCancel}
            type="button"
            variant="outlined"
          >
            {t('create_stream_form.new_analytics.on_cancel')}
          </Control.Button>
        </div>
      </div>
    </form>
  );
}

NewAnalyticsModal.displayName = 'NewAnalyticsModal';

export { NewAnalyticsModal };
